/**
 * A saved result that is not the latest calculation: an exclamation glyph and
 * a short label ("Not latest", or "Updating" when the page knows a
 * recalculation is in progress). The glyph and the words carry the meaning,
 * never colour alone. Styles: `.stale-mark` in theme/features/register.css
 * (contextual sizes in the feature stylesheets).
 *
 * Where the page has no visible reason line (register cards, the table, the
 * lot glance and thesis) the mark is `interactive`: a focusable button whose
 * description is the reasons in plain words and whose popover shows them to
 * keyboard, touch and mouse users. Inside a link (the card photo overlay) it
 * stays a plain glyph whose accessible name carries the same words. The text
 * is exposed once (no duplicate `title`).
 *
 * Reasons are the server's stale reasons (`recipe`, `catalogue`, `plan`,
 * `profile`, `selection`, `source`, `card`, `override`); an older server sends
 * none, and an unknown value is described generically.
 */
import { useId, useState } from 'react'
import { Popover } from './kit'

const REASON_TEXT: Record<string, string> = {
  recipe: 'the model changed',
  catalogue: 'the fact catalogue changed',
  plan: 'the calculation plan changed',
  profile: 'your settings changed',
  selection: 'the refurbishment choice changed',
  source: 'the listing data changed',
  card: 'the property card was rebuilt',
  override: 'your property values changed',
}
const UNKNOWN_REASON = 'something it depends on changed'

/** The reasons in plain words, in the order sent, each phrase once. */
export function staleReasonPhrases(reasons: readonly string[] | null | undefined): string[] {
  const phrases: string[] = []
  for (const reason of reasons ?? []) {
    const phrase = (Object.prototype.hasOwnProperty.call(REASON_TEXT, reason) ? REASON_TEXT[reason] : undefined) ?? UNKNOWN_REASON
    if (!phrases.includes(phrase)) phrases.push(phrase)
  }
  return phrases
}

/** "a", "a and b", "a, b and c". */
export function joinPhrases(phrases: readonly string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? ''
  return `${phrases.slice(0, -1).join(', ')} and ${phrases[phrases.length - 1]}`
}

/** "Since it was saved, the model changed and your settings changed." or '' without reasons. */
export function staleReasonSentence(reasons: readonly string[] | null | undefined): string {
  const phrases = staleReasonPhrases(reasons)
  return phrases.length ? `Since it was saved, ${joinPhrases(phrases)}.` : ''
}

/** The mark's accessible name (plain mark) or description (interactive mark). */
export function staleMarkText(reasons: readonly string[] | null | undefined, updating = false): string {
  const lead = updating ? 'Updating: a new calculation is in progress. This is the last saved result.' : 'Not the latest result: this is the last saved calculation.'
  const why = staleReasonSentence(reasons)
  return why ? `${lead} ${why}` : lead
}

/**
 * The reason line for a saved result that is not the latest: it is the last
 * saved one, why it is no longer current, and that values may change. Without
 * reasons (an older server) or while a recalculation runs, it says a new
 * calculation is being prepared.
 */
export function lastSavedResultLine(reasons: readonly string[] | null | undefined, updating = false): string {
  const why = staleReasonSentence(reasons)
  const lead = updating || !why ? 'Showing the last saved result while a new calculation is prepared.' : 'Showing the last saved result.'
  return [lead, why, 'Values may change.'].filter(Boolean).join(' ')
}

function StaleGlyph() {
  return <svg className="stale-mark-ic" viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeLinecap="round">
    <circle cx="8" cy="8" r="6.6" strokeWidth="1.5" />
    <path d="M8 4.6v4.3" strokeWidth="1.7" />
    <path d="M8 11.4v.05" strokeWidth="2" />
  </svg>
}

export function StaleMark({ reasons, updating = false, compact = false, interactive = false }: {
  reasons?: readonly string[] | null
  /** The page knows a recalculation is in progress: the label reads "Updating". */
  updating?: boolean
  /** Glyph only (the label stays in the accessible name), for tight overlays such as the card photo strip. */
  compact?: boolean
  /** A focusable trigger with a popover of the reasons, for places without a visible reason line. Never inside a link. */
  interactive?: boolean
}) {
  const [open, setOpen] = useState(false)
  const descriptionId = useId()
  const text = staleMarkText(reasons, updating)
  const label = updating ? 'Updating' : 'Not latest'
  const state = updating ? 'updating' : 'stale'
  if (interactive) {
    return <Popover open={open} onClose={() => setOpen(false)} align="start" width={260} anchor={
      <button type="button" className="stale-mark is-trigger" data-state={state} aria-haspopup="dialog" aria-expanded={open} aria-describedby={descriptionId}
        onClick={event => { event.preventDefault(); event.stopPropagation(); setOpen(value => !value) }}>
        <StaleGlyph /><span>{label}</span>
        <span id={descriptionId} hidden>{text}</span>
      </button>
    }>
      <p className="pop-note stale-mark-pop">{text}</p>
    </Popover>
  }
  return <span className={compact ? 'stale-mark is-compact' : 'stale-mark'} data-state={state} role="img" aria-label={text}>
    <StaleGlyph />
    {compact ? null : <span aria-hidden="true">{label}</span>}
  </span>
}
